import Fuse from 'fuse.js'

interface FuzzyKeyConfig {
    name: string
    weight?: number
    threshold?: number
}

interface SimpleFuzzyOptions {
    keys: string[]
    includeScore?: boolean
    threshold?: number
    isCaseSensitive?: boolean
}

interface ComplexFuzzyOptions {
    keys: FuzzyKeyConfig[]
    includeScore?: boolean
    threshold?: number
    isCaseSensitive?: boolean
}

type FuzzyOptions = SimpleFuzzyOptions | ComplexFuzzyOptions

const isComplexKeys = (
    keys: string[] | FuzzyKeyConfig[]
): keys is FuzzyKeyConfig[] => {
    return keys.length > 0 && typeof keys[0] === 'object'
}

const useFuzzy = <T>(
    items: T[],
    search: string,
    options: FuzzyOptions
): {
    results: T[]
} => {
    const fuseConfig: any = {
        isCaseSensitive: false,
        ...options,
    }

    if (isComplexKeys(options.keys)) {
        const allResults: T[] = []
        const seenItems = new Set<T>()

        options.keys.forEach((keyConfig) => {
            const individualFuse = new Fuse(items, {
                keys: [
                    {
                        name: keyConfig.name,
                        weight: keyConfig.weight ?? 1,
                    },
                ],
                threshold: keyConfig.threshold ?? 0.5,
                isCaseSensitive: false,
            })

            const keyResults = individualFuse
                .search(search)
                .map(({ item }) => item)

            keyResults.forEach((item) => {
                if (!seenItems.has(item)) {
                    seenItems.add(item)
                    allResults.push(item)
                }
            })
        })

        return { results: allResults }
    }

    const fuse = new Fuse(items, fuseConfig)
    const results = fuse.search(search).map(({ item }) => item)

    return { results }
}

export default useFuzzy
